import React, { useState, useEffect } from 'react';
import StylePicker from './StylePicker';
import { AVAILABLE_MODELS, fetchAvailableModels } from '../utils/googleAI';
import { generateGif } from '../utils/gifGenerator';

const Sidebar = ({
  apiKey,
  isApiKeySet,
  onApiKeySet,
  onApiKeyOverride,
  conversation,
  currentTurn,
  style,
  onStyleChange,
  model,
  onModelChange,
  onContinue,
  isLoading,
  error,
  success,
  onClearMessages,
  onOpenGallery,
  onPublishCloud,
  onNewThread,
  onAddToGallery,
  onDetachFork,
  gallery,
  isCollapsed,
  onToggle,
  isRemote,
  onForkCloud,
  isModal,
  threadId // For GIF export filename
}) => {
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [overrideKeyInput, setOverrideKeyInput] = useState('');
  const [showOverride, setShowOverride] = useState(false);
  const [availableModels, setAvailableModels] = useState(AVAILABLE_MODELS);
  const [loadingModels, setLoadingModels] = useState(false);
  const [isGeneratingGif, setIsGeneratingGif] = useState(false);
  const [gifProgress, setGifProgress] = useState(0);

  const handleDetachFork = () => {
    if (window.confirm('Remove fork information from this thread? This makes it a standalone thread.')) {
      if (onDetachFork) {
        onDetachFork();
      }
    }
  };

  // Fetch available models when API key changes
  useEffect(() => {
    const loadModels = async () => {
      if (isApiKeySet && apiKey) {
        setLoadingModels(true);
        const fetchedModels = await fetchAvailableModels(apiKey);
        if (fetchedModels.length > 0) {
          console.log('Using fetched models from API:', fetchedModels);
          setAvailableModels(fetchedModels);
          // Keep current selection if still available; otherwise fall back to first.
          const stillValid = !!fetchedModels.find(m => m.id === model);
          if (!stillValid) onModelChange(fetchedModels[0].id);
        }
        setLoadingModels(false);
      }
    };
    loadModels();
    // We intentionally don't include onModelChange/model in deps to avoid loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApiKeySet, apiKey]);

  const handleApiKeySubmit = (e) => {
    e.preventDefault();
    if (apiKeyInput.trim()) {
      onApiKeySet(apiKeyInput.trim());
      setApiKeyInput('');
      onClearMessages();
    }
  };

  const handleOverrideSubmit = (e) => {
    e.preventDefault();
    console.log('🔄 Sidebar: Override form submitted');
    onApiKeyOverride(overrideKeyInput);
    setOverrideKeyInput('');
    setShowOverride(false);
    onClearMessages();
  };

  // Export functionality
  const handleExport = () => {
    const data = {
      conversation,
      style,
      exportDate: new Date().toISOString(),
      appVersion: "1.0"
    };

    // Convert to JSON string
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    // Trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = `details-matter-session-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleGifExport = async () => {
    if (isGeneratingGif) return;
    setIsGeneratingGif(true);
    setGifProgress(0);
    try {
      const blob = await generateGif(conversation, (p) => setGifProgress(p));
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `details-matter-${threadId || 'thread'}-${new Date().toISOString().slice(0, 10)}.gif`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("GIF generation failed", err);
      alert("Failed to generate GIF: " + err.message);
    } finally {
      setIsGeneratingGif(false);
      setGifProgress(0);
    }
  };

  // Import functionality
  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const importedData = JSON.parse(event.target.result);

        // Dispatch custom event to update parent state (simplest way without prop drilling setConversation)
        const customEvent = new CustomEvent('importSession', { detail: importedData });
        window.dispatchEvent(customEvent);

        // Reset file input
        e.target.value = '';
      } catch (err) {
        console.error("Failed to parse imported file", err);
        alert("Invalid session file. Please upload a valid JSON export.");
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      {!isModal && (
        <button
          className="sidebar-toggle"
          onClick={onToggle}
          title={isCollapsed ? 'Expand Settings' : 'Collapse Settings'}
        >
          {isCollapsed ? '⚙️' : '◀ Hide'}
        </button>
      )}

      {!isCollapsed && !isModal && <h2>⚙️ Settings</h2>}

      {!isCollapsed && (
        <>
          {/* API Key / Auth Section */}
          <div className="section">
            <h3>Authentication</h3>

            {!isApiKeySet ? (
              <>
                <div className="warning" style={{ marginBottom: '12px' }}>
                  <strong>Note:</strong> You need your own Gemini API Key.
                </div>

                <a
                  href="https://aistudio.google.com/app/apikey"
                  target="_blank"
                  rel="noreferrer"
                  className="secondary-button"
                  style={{ width: '100%', marginBottom: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', textDecoration: 'none', background: 'var(--background-secondary)', boxSizing: 'border-box' }}
                >
                  🔑 Get API Key (Google AI Studio)
                </a>

                <div style={{ textAlign: 'center', margin: '12px 0', fontSize: '0.8em', color: '#666' }}>THEN</div>

                <form onSubmit={handleApiKeySubmit} style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: 0 }}>
                  <input
                    type="password"
                    placeholder="Paste Gemini API Key"
                    value={apiKeyInput}
                    onChange={(e) => setApiKeyInput(e.target.value)}
                    required
                    style={{ width: '100%', boxSizing: 'border-box', margin: 0 }}
                  />
                  <button type="submit" className="primary-button" style={{ width: '100%', margin: 0 }}>Set Key</button>
                </form>
              </>
            ) : (
              <>
                <div className="success">
                  ✅ API Key Active
                </div>

                {!showOverride ? (
                  <button
                    onClick={() => setShowOverride(true)}
                    className="secondary-button"
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    Change API Key
                  </button>
                ) : (
                  <div style={{ marginTop: '12px' }}>
                    <form onSubmit={handleOverrideSubmit}>
                      <input
                        type="password"
                        placeholder="New API Key"
                        value={overrideKeyInput}
                        onChange={(e) => setOverrideKeyInput(e.target.value)}
                        required
                        style={{ marginBottom: '8px' }}
                      />
                      <button type="submit" className="primary-button" style={{ width: '100%', marginBottom: '8px' }}>Update Key</button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowOverride(false);
                          setOverrideKeyInput('');
                        }}
                        className="secondary-button"
                        style={{ width: '100%', justifyContent: 'center' }}
                      >
                        Cancel
                      </button>
                    </form>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Messages */}
          {error && <div className="error">{error}</div>}
          {success && <div className="success">{success}</div>}

          {/* Share / Gallery Section */}
          {isApiKeySet && (
            <div className="section">
              <h3>🏛️ Exhibition & Gallery</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button
                  className="danger-button"
                  onClick={onNewThread}
                  disabled={isLoading}
                  title="Start a completely new thread (your current thread is auto-saved to gallery)"
                >
                  🧹 New Thread
                </button>

                {conversation.length > 0 && (
                  <button
                    className="secondary-button"
                    onClick={() => {
                      const event = new CustomEvent('undoLastTurn');
                      window.dispatchEvent(event);
                    }}
                    disabled={conversation.length === 0 || isLoading}
                    title="Remove the last turn from the thread"
                  >
                    ↩️ Undo Last Turn
                  </button>
                )}

                {isRemote && (
                  <button
                    className="primary-button"
                    onClick={onForkCloud}
                    disabled={isLoading}
                    style={{
                      background: 'linear-gradient(to bottom, #c5a059, #8b6914)',
                      color: '#ffffff',
                      border: '1px solid #d4b06a',
                      textShadow: '0 1px 2px rgba(0,0,0,0.4)'
                    }}
                    title="Create your own copy of this published thread to edit"
                  >
                    🌱 Fork this Thread
                  </button>
                )}

                {!isRemote && conversation.length > 0 && (
                  <button
                    className="secondary-button"
                    onClick={onPublishCloud}
                    disabled={isLoading}
                    title="Publish to the Grand Exhibition"
                  >
                    🏛️ Publish to the Exhibition
                  </button>
                )}

                {onDetachFork && (
                  <button
                    className="secondary-button"
                    onClick={handleDetachFork}
                    style={{ fontSize: '0.8rem', padding: '8px' }}
                    title="Fix: Remove incorrect fork association"
                  >
                    ✂️ Detach / Unfork
                  </button>
                )}
              </div>
            </div>
          )}

          {/* AI Model Section */}
          {isApiKeySet && (
            <div className="section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>🧠 AI Model</h3>
                {loadingModels && <span className="loading" style={{ fontSize: '0.75rem' }}>Fetching...</span>}
              </div>
              <select
                value={model}
                onChange={(e) => onModelChange(e.target.value)}
                disabled={loadingModels}
              >
                {availableModels.map(modelOption => (
                  <option key={modelOption.id} value={modelOption.id}>
                    {modelOption.name}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '6px' }}>
                {availableModels.length > 3 ? 'Models loaded from your key.' : 'Flash is faster/cheaper. Pro is higher quality.'}
              </div>
            </div>
          )}

          {/* Art Style Section */}
          {isApiKeySet && (
            <div className="section">
              <h3>🎨 Styles</h3>
              <StylePicker
                selectedStyles={style}
                onStyleChange={onStyleChange}
                disabled={isLoading}
              />
            </div>
          )}

          {/* Storage & Sharing */}
          {conversation.length > 0 && (
            <div className="section">
              <h3>💾 Save / Load</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  onClick={handleGifExport}
                  className="primary-button"
                  disabled={isGeneratingGif}
                  style={{ background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)', border: 'none' }}
                >
                  {isGeneratingGif ? `🎬 Generating GIF (${Math.round(gifProgress * 100)}%)...` : '🎬 Export as GIF'}
                </button>

                <button
                  onClick={handleExport}
                  className="primary-button"
                  style={{ background: 'var(--success-bg)', color: 'var(--success-text)', border: '1px solid var(--success-text)' }}
                >
                  📥 Export Session (JSON)
                </button>

                <div style={{ position: 'relative' }}>
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleImport}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      opacity: 0,
                      width: '100%',
                      height: '100%',
                      cursor: 'pointer'
                    }}
                  />
                  <button
                    className="primary-button"
                    style={{ width: '100%', pointerEvents: 'none', background: 'var(--background-color)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                  >
                    📤 Import Session
                  </button>
                </div>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  Share the JSON file to let others fork your thread.
                </p>
              </div>
            </div>
          )}

          {/* Stats */}
          {conversation.length > 0 && (
            <div className="section">
              <h3>📊 Stats</h3>
              <div className="stats">
                <div className="stat">
                  <div className="stat-value">{currentTurn}</div>
                  <div className="stat-label">Turns</div>
                </div>
                <div className="stat">
                  <div className="stat-value">{conversation.length}</div>
                  <div className="stat-label">Total</div>
                </div>
              </div>
            </div>
          )}


        </>
      )}
    </div>
  );
};

export default Sidebar;
